import { Entity, ExploreSingleInsight } from '@toggle/toggle';
import { isToday } from 'date-fns';
import React, { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { InsightsTooltip } from '~/views/insights/components/insights-tooltip/InsightsTooltip';

import * as Shared from '../explore-table/ExploreTable.styles';
import { InsightIconsRoot } from '../insight-icons-root/InsightIconsRoot';
import { Count } from './count/Count';

export interface TruncateSingleIconsProps {
  insights: ExploreSingleInsight[];
  maxWidth?: number;
  handleSelect?: (insightId: string, entity: Entity) => void;
  entity?: Entity;
  variant?: 'chart' | 'assets-table';
}

const CHART_SINGLE_WIDTH = 24;
const CHART_SINGLE_HEIGHT = 28;

export const TABLE_SINGLE_WIDTH = 18;
export const TABLE_SINGLE_HEIGHT = 20;

export const TruncateSingleIcons = ({
  insights,
  maxWidth,
  handleSelect,
  entity,
  variant = 'chart',
}: TruncateSingleIconsProps) => {
  const { t } = useTranslation(['insights']);
  const width = variant === 'chart' ? CHART_SINGLE_WIDTH : TABLE_SINGLE_WIDTH;
  const SINGLE_SIZE = {
    width: `${width}px`,
    height:
      variant === 'chart'
        ? `${CHART_SINGLE_HEIGHT}px`
        : `${TABLE_SINGLE_HEIGHT}px`,
  };

  const conditions = insights.flatMap(insight => {
    if (insight.conditions.length === 0) {
      return [];
    }

    return {
      ...insight.conditions[0],
      direction: insight.direction,
      createdAt: isToday(new Date(insight.created_at)),
      insightId: insight.id,
      matched: insight.matched,
      bestHorizon: insight.best_horizon,
      episodesCount: insight.episode_count,
      median: insight.median,
      stars: insight.stars,
      name: insight.card.name,
    };
  });

  const visible = maxWidth
    ? conditions.slice(0, Math.floor(maxWidth / width))
    : conditions;
  const shouldTruncate = conditions.length > visible.length;

  return (
    <Shared.InsightsWrapper data-testid="truncated-single-icons">
      {visible.map((c, i) => {
        const episodeLabel = t('insights:insightCard.episode', {
          count: c.episodesCount,
        });

        return (
          <InsightsTooltip
            key={`${i}${c.category}`}
            conditions={[c]}
            direction={c.direction}
            customSize={{ width: '27px', height: '32px' }}
            isStarred={c.createdAt}
            horizon={c.bestHorizon}
            episodeLabel={episodeLabel}
            median={c.median}
            stars={c.stars}
            name={c.name}
            entityTag={entity?.tag}
          >
            <div>
              <InsightIconsRoot
                key={`${i}${c.category}`}
                conditions={[c]}
                direction={c.direction}
                customSize={SINGLE_SIZE}
                isStarred={c.createdAt}
                isMatched={c.matched}
                onIconClick={(e: SyntheticEvent) => {
                  e.stopPropagation();
                  handleSelect && entity
                    ? handleSelect(c.insightId, entity)
                    : null;
                }}
                isSparklesCondensed={variant === 'assets-table'}
              />
            </div>
          </InsightsTooltip>
        );
      })}

      {shouldTruncate && <Count count={conditions.length - visible.length} />}
    </Shared.InsightsWrapper>
  );
};
